<template>
  <section
    v-motion
    class="border-b border-charcoal-blue"
    aria-label="Hero"
    :initial="{ opacity: 0, y: 24 }"
    :enter="{ opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }"
  >
    <div class="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[min(60vh,400px)] md:min-h-[700px]">
      <div class="md:col-span-7 relative border-r border-charcoal-blue overflow-hidden px-4 py-8 sm:px-6 md:p-12 flex flex-col justify-center bg-concrete-gray">
        <div class="relative z-20">
          <button
            v-if="logoUrl && canOpenOwnerLogoModal"
            type="button"
            class="mb-6 inline-block border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2"
            :aria-label="$t('public.ownerEditLogoAria')"
            data-testid="public-hero-logo"
            @click="openOwnerLogoModal"
          >
            <img :src="logoUrl" :alt="name || 'Restaurant'" class="w-40 md:w-64 border  border-charcoal-blue" />
          </button>
          <div v-else-if="logoUrl" class="mb-6" data-testid="public-hero-logo">
            <img :src="logoUrl" :alt="name || 'Restaurant'" class="w-40 md:w-64 border  border-charcoal-blue" />
          </div>
          <button
            v-else-if="viewer?.is_owner && ownerViewMode && canOpenOwnerLogoModal"
            type="button"
            class="mb-6 block max-w-[200px] rounded border-0 bg-transparent p-0 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2"
            :aria-label="$t('public.ownerEditLogoAria')"
            @click="openOwnerLogoModal"
          >
            <div class="py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5">
              <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.noLogoSetOwnerMessage') }}</p>
            </div>
          </button>
          <div v-else-if="viewer?.is_owner && ownerViewMode" class="mb-6 max-w-[200px] py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5">
            <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.noLogoSetOwnerMessage') }}</p>
          </div>
          <div class="flex flex-wrap items-center gap-3 mb-6">
            <div class="inline-block bg-charcoal-blue text-white px-4 py-1 heading-utilitarian text-xs font-bold">EST. {{ displayYear }} / SPECIFICATION</div>
            <PublicOpenStatusBadge :operating-hours="operatingHours" variant="t2-hero" />
          </div>
          <h1 class="heading-utilitarian break-words text-7xl sm:text-8xl md:text-9xl lg:text-9xl font-extrabold mb-8 leading-[0.85] text-charcoal-blue">
            <template v-if="name">
              {{ name.split(' ').slice(0, -1).join(' ') }}
              <template v-if="name.split(' ').length > 1"> </template>
              <span class="rms-t2-accent">{{ name.split(' ').at(-1) }}</span>
            </template>
          </h1>
          <div v-if="heroSubtext" class="max-w-md border-l-8 rms-t2-accent-border pl-8 py-2">
            <p class="text-lg font-body leading-relaxed text-charcoal-blue/80">
              {{ heroSubtext }}
            </p>
          </div>
          <div
            v-else-if="viewer?.is_owner && ownerViewMode"
            class="max-w-md py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5"
          >
            <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.noTaglineOrDescriptionSetOwnerMessage') }}</p>
          </div>
          <div class="flex flex-wrap gap-3 mt-8">
            <a
              href="#menu"
              class="heading-utilitarian min-h-[44px] px-6 py-3 font-bold text-sm text-white rms-t2-accent-bg hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2 inline-flex items-center justify-center"
            >
              {{ viewListLabel || $t('public.t2MenuNav') }}
            </a>
            <a
              v-if="hasAbout"
              href="#about"
              class="heading-utilitarian min-h-[44px] px-6 py-3 font-bold text-sm border-2 border-charcoal-blue text-charcoal-blue hover:bg-charcoal-blue/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2 inline-flex items-center justify-center"
            >
              {{ $t('public.t3NavAbout') }}
            </a>
            <div
              v-else-if="viewer?.is_owner && ownerViewMode"
              class="py-3 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5 max-w-[200px]"
            >
              <p class="text-xs font-mono text-charcoal-blue/70">{{ $t('public.ownerNavAboutUnavailable') }}</p>
            </div>
          </div>
        </div>
        <div class="absolute inset-0 opacity-5 pointer-events-none bg-[length:20px_20px]" style="background-image: radial-gradient(#1e293b 1px, transparent 1px);" aria-hidden="true" />
      </div>
      <div class="md:col-span-5 min-h-[200px] relative bg-charcoal-blue group overflow-hidden">
        <img
          v-if="bannerUrl"
          :src="bannerUrl"
          :alt="name ? `${name} — restaurant interior` : 'Restaurant interior'"
          class="w-full h-full object-cover grayscale opacity-80 md:hidden group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
          loading="eager"
        />
        <div v-else class="w-full h-full flex items-center justify-center text-charcoal-blue/40 text-sm">
        </div>
        <div v-if="bannerUrl" class="absolute bottom-6 right-6 bg-white/90 backdrop-blur-sm p-4 border border-charcoal-blue text-[10px] font-mono uppercase tracking-widest text-charcoal-blue">
          Fig 1.0 // Interior Vista
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()

const props = defineProps({
  name: { type: String, default: '' },
  tagline: { type: String, default: '' },
  description: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
  bannerUrl: { type: String, default: '' },
  yearEstablished: { type: Number, default: null },
  operatingHours: { type: Object, default: null },
  viewListLabel: { type: String, default: '' },
  hasAbout: { type: Boolean, default: false },
})

const currentYear = computed(() => new Date().getFullYear())
const displayYear = computed(() => props.yearEstablished ?? currentYear.value)
/** Restaurant tagline if set, otherwise restaurant description. */
const heroSubtext = computed(() => (props.tagline || props.description || '').trim() || '')
</script>
